import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './SettingsTabs.css';

const INITIAL_BRANCH_FORM = {
  branch_name: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  contact_person: '',
  contact_email: '',
  contact_phone: ''
};
const INITIAL_GRADE_LEVEL_FORM = {
  name: '',
  description: ''
};
const INITIAL_GRADE_NAME_FORM = {
  level_id: '',
  name: '',
  description: ''
};
const INITIAL_SCALE_FORM = {
  name: '',
  description: ''
};
const INITIAL_SCALE_ENTRY_FORM = {
  scale_id: '',
  code: '',
  label: '',
  min_percentage: '',
  max_percentage: ''
};

function Settings() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [formData, setFormData] = useState({
    school_name: '',
    school_type: 'public',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    principal_name: '',
    principal_email: '',
    total_students: 0,
    total_teachers: 0,
    description: '',
    currency: 'PKR'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [activeTab, setActiveTab] = useState('school');
  const [branches, setBranches] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState('');
  const [branchForm, setBranchForm] = useState(INITIAL_BRANCH_FORM);
  const [branchSaving, setBranchSaving] = useState(false);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [gradeLevelForm, setGradeLevelForm] = useState(INITIAL_GRADE_LEVEL_FORM);
  const [gradeLevelSaving, setGradeLevelSaving] = useState(false);
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeNameForm, setGradeNameForm] = useState(INITIAL_GRADE_NAME_FORM);
  const [gradeNameSaving, setGradeNameSaving] = useState(false);
  const [gradeScales, setGradeScales] = useState([]);
  const [scaleForm, setScaleForm] = useState(INITIAL_SCALE_FORM);
  const [scaleSaving, setScaleSaving] = useState(false);
  const [gradeScaleEntries, setGradeScaleEntries] = useState({});
  const [scaleEntryForm, setScaleEntryForm] = useState(INITIAL_SCALE_ENTRY_FORM);
  const [scaleEntrySaving, setScaleEntrySaving] = useState(false);
  const [selectedScaleId, setSelectedScaleId] = useState(null);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Function to get valid file URL (tries signed URL first, then public URL)
  const getValidFileUrl = async (url) => {
    if (!url) return null;
    
    try {
      // If it's a data URL (local preview), return as is
      if (url.startsWith('data:')) {
        return url;
      }
      
      // Extract file path from Supabase URL
      let filePath = null;
      const patterns = [
        /\/storage\/v1\/object\/public\/student-images\/(.+)$/,
        /\/storage\/v1\/object\/student-images\/(.+)$/,
        /student-images\/(.+)$/
      ];
      
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          filePath = match[1];
          break;
        }
      }
      
      // If we found a file path, try to generate signed URL
      if (filePath) {
        // Clean the file path - remove any query parameters
        const cleanPath = filePath.split('?')[0];
        
        try {
          const { data, error } = await supabase.storage
            .from('student-images')
            .createSignedUrl(cleanPath, 3600); // Valid for 1 hour
          
          if (!error && data?.signedUrl) {
            console.log('Generated signed URL for logo:', data.signedUrl);
            return data.signedUrl;
          } else {
            console.log('Signed URL failed, using public URL');
            // Try public URL as fallback
            const { data: publicData } = supabase.storage
              .from('student-images')
              .getPublicUrl(cleanPath);
            return publicData?.publicUrl || url;
          }
        } catch (signError) {
          console.error('Error creating signed URL:', signError);
          // Try public URL as fallback
          const { data: publicData } = supabase.storage
            .from('student-images')
            .getPublicUrl(filePath.split('?')[0]);
          return publicData?.publicUrl || url;
        }
      }
      
      // If all else fails, return original URL
      return url;
    } catch (err) {
      console.error('Error generating valid file URL:', err);
      return url; // Return original URL as fallback
    }
  };

  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        // Get school data from localStorage first
        const storedSchoolData = localStorage.getItem('schoolData');
        if (storedSchoolData) {
          const parsedData = JSON.parse(storedSchoolData);
          setSchoolData(parsedData);
          
          // Set logo preview from localStorage immediately (before Supabase fetch)
          if (parsedData.logo_url) {
            // Try to get valid URL (signed URL if needed)
            const validUrl = await getValidFileUrl(parsedData.logo_url);
            setLogoPreview(validUrl);
            console.log('Logo preview set from localStorage (initial):', validUrl);
          }
          
          // Also fetch fresh data from Supabase
          if (parsedData.id) {
            const { data, error } = await supabase
              .from('schools')
              .select('*')
              .eq('id', parsedData.id)
              .single();
            
            if (!error && data) {
              setSchoolData(data);
              // Update form data
              setFormData({
                school_name: data.school_name || '',
                school_type: data.school_type || 'public',
                phone: data.phone || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                zip_code: data.zip_code || '',
                website: data.website || '',
                principal_name: data.principal_name || '',
                principal_email: data.principal_email || '',
                total_students: data.total_students || 0,
                total_teachers: data.total_teachers || 0,
                description: data.description || '',
                currency: data.currency || 'PKR'
              });
              
              // Set logo preview if logo exists
              if (data.logo_url) {
                // Try to get valid URL (signed URL if needed)
                const validUrl = await getValidFileUrl(data.logo_url);
                setLogoPreview(validUrl);
                console.log('Logo preview set from Supabase:', validUrl);
              } else {
                setLogoPreview(null);
              }
              // Update localStorage with fresh data
              localStorage.setItem('schoolData', JSON.stringify(data));
            } else {
              // Use localStorage data if Supabase fetch fails
              setFormData({
                school_name: parsedData.school_name || '',
                school_type: parsedData.school_type || 'public',
                phone: parsedData.phone || '',
                address: parsedData.address || '',
                city: parsedData.city || '',
                state: parsedData.state || '',
                zip_code: parsedData.zip_code || '',
                website: parsedData.website || '',
                principal_name: parsedData.principal_name || '',
                principal_email: parsedData.principal_email || '',
                total_students: parsedData.total_students || 0,
                total_teachers: parsedData.total_teachers || 0,
                description: parsedData.description || ''
              });
              
              // Set logo preview from localStorage if logo exists
              if (parsedData.logo_url) {
                // Try to get valid URL (signed URL if needed)
                const validUrl = await getValidFileUrl(parsedData.logo_url);
                setLogoPreview(validUrl);
                console.log('Logo preview set from localStorage fallback:', validUrl);
              } else {
                setLogoPreview(null);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching school data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, []);

  useEffect(() => {
    if (!schoolData?.id) {
      setBranches([]);
      return;
    }

    const fetchBranches = async () => {
      setBranchLoading(true);
      setBranchError('');

      try {
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Error fetching branches:', error);
          setBranchError('Failed to load branch data');
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Exception fetching branches:', err);
        setBranchError('Failed to load branch data');
      } finally {
        setBranchLoading(false);
      }
    };

    fetchBranches();
  }, [schoolData?.id]);

  useEffect(() => {
    if (!schoolData?.id) return;
    fetchGradeLevels();
    fetchGradeNames();
    fetchGradeScales();
    fetchGradeScaleEntries();
  }, [schoolData?.id]);

  useEffect(() => {
    if (!gradeNameForm.level_id && gradeLevels.length > 0) {
      setGradeNameForm(prev => ({ ...prev, level_id: gradeLevels[0].id }));
    }
  }, [gradeLevels, gradeNameForm.level_id]);

  useEffect(() => {
    if (!selectedScaleId && gradeScales.length > 0) {
      const firstScaleId = gradeScales[0].id;
      setSelectedScaleId(firstScaleId);
      setScaleEntryForm(prev => ({ ...prev, scale_id: firstScaleId }));
    }
  }, [gradeScales, selectedScaleId]);

  useEffect(() => {
    if (selectedScaleId) {
      setScaleEntryForm(prev => ({ ...prev, scale_id: selectedScaleId }));
    }
  }, [selectedScaleId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBranchInputChange = (e) => {
    const { name, value } = e.target;
    setBranchForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      e.target.value = ''; // Reset input
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      e.target.value = ''; // Reset input
      return;
    }

    setLogoFile(file);

    // Create preview immediately
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
      console.log('Logo preview set successfully');
    };
    reader.onerror = () => {
      console.error('Error reading file');
      alert('Error reading image file. Please try again.');
      e.target.value = ''; // Reset input
      setLogoFile(null);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('logo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const fetchGradeLevels = async () => {
    if (!schoolData?.id) return;
    try {
      const { data, error } = await supabase
        .from('grade_levels')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setGradeLevels(data || []);
    } catch (err) {
      console.error('Error fetching grade levels:', err);
    }
  };

  const fetchGradeNames = async () => {
    if (!schoolData?.id) return;
    try {
      const { data, error } = await supabase
        .from('grade_names')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setGradeNames(data || []);
    } catch (err) {
      console.error('Error fetching grade names:', err);
    }
  };

  const fetchGradeScales = async () => {
    if (!schoolData?.id) return;
    try {
      const { data, error } = await supabase
        .from('grade_scales')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setGradeScales(data || []);
    } catch (err) {
      console.error('Error fetching grade scales:', err);
    }
  };

  const fetchGradeScaleEntries = async () => {
    if (!schoolData?.id) return;
    try {
      const { data, error } = await supabase
        .from('grade_scale_entries')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const grouped = (data || []).reduce((acc, entry) => {
        const list = acc[entry.scale_id] || [];
        return {
          ...acc,
          [entry.scale_id]: [...list, entry]
        };
      }, {});

      setGradeScaleEntries(grouped);
    } catch (err) {
      console.error('Error fetching grade scale entries:', err);
    }
  };

  const parsePercentageValue = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = value.toString().trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleGradeLevelChange = (e) => {
    const { name, value } = e.target;
    setGradeLevelForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGradeNameChange = (e) => {
    const { name, value } = e.target;
    setGradeNameForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScaleChange = (e) => {
    const { name, value } = e.target;
    setScaleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScaleSelection = (e) => {
    const value = e.target.value;
    setSelectedScaleId(value ? Number(value) : null);
  };

  const handleScaleEntryChange = (e) => {
    const { name, value } = e.target;
    setScaleEntryForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGradeLevelSubmit = async (e) => {
    e.preventDefault();
    if (!gradeLevelForm.name.trim()) {
      alert('Grade level name is required');
      return;
    }

    if (!schoolData?.id) {
      alert('School information unavailable');
      return;
    }

    setGradeLevelSaving(true);
    try {
      const { data, error } = await supabase
        .from('grade_levels')
        .insert({
          school_id: schoolData.id,
          name: gradeLevelForm.name.trim(),
          description: gradeLevelForm.description.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      setGradeLevels(prev => [...prev, data]);
      setGradeLevelForm(INITIAL_GRADE_LEVEL_FORM);
    } catch (err) {
      console.error('Error saving grade level:', err);
      alert('Unable to save grade level right now. Please try again.');
    } finally {
      setGradeLevelSaving(false);
    }
  };

  const handleGradeNameSubmit = async (e) => {
    e.preventDefault();
    if (!gradeNameForm.name.trim()) {
      alert('Grade name is required');
      return;
    }
    if (!gradeNameForm.level_id) {
      alert('Select a grade level first');
      return;
    }

    if (!schoolData?.id) {
      alert('School information unavailable');
      return;
    }

    setGradeNameSaving(true);
    try {
      const levelId = Number(gradeNameForm.level_id);
      if (!Number.isFinite(levelId)) {
        alert('Select a valid grade level');
        setGradeNameSaving(false);
        return;
      }

      const { data, error } = await supabase
        .from('grade_names')
        .insert({
          school_id: schoolData.id,
          level_id: levelId,
          name: gradeNameForm.name.trim(),
          description: gradeNameForm.description.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      setGradeNames(prev => [...prev, data]);
      setGradeNameForm(prev => ({
        ...prev,
        name: '',
        description: ''
      }));
    } catch (err) {
      console.error('Error saving grade name:', err);
      alert('Unable to save grade name right now. Please try again.');
    } finally {
      setGradeNameSaving(false);
    }
  };

  const handleScaleSubmit = async (e) => {
    e.preventDefault();
    if (!scaleForm.name.trim()) {
      alert('Grade scale name is required');
      return;
    }
    if (!schoolData?.id) {
      alert('School information unavailable');
      return;
    }

    setScaleSaving(true);
    try {
      const { data, error } = await supabase
        .from('grade_scales')
        .insert({
          school_id: schoolData.id,
          name: scaleForm.name.trim(),
          description: scaleForm.description.trim() || null
        })
        .select()
        .single();

      if (error) throw error;

      setGradeScales(prev => [...prev, data]);
      setSelectedScaleId(data.id);
      setScaleEntryForm(prev => ({ ...prev, scale_id: data.id }));
      setScaleForm(INITIAL_SCALE_FORM);
    } catch (err) {
      console.error('Error saving grade scale:', err);
      alert('Unable to save grade scale right now. Please try again.');
    } finally {
      setScaleSaving(false);
    }
  };

  const handleScaleEntrySubmit = async (e) => {
    e.preventDefault();
    if (!scaleEntryForm.scale_id) {
      alert('Select a grade scale first');
      return;
    }
    if (!scaleEntryForm.label.trim() && !scaleEntryForm.code.trim()) {
      alert('Provide at least a label or code for the scale entry');
      return;
    }
    if (!schoolData?.id) {
      alert('School information unavailable');
      return;
    }

    setScaleEntrySaving(true);
    try {
      const scaleId = Number(scaleEntryForm.scale_id);
      if (!Number.isFinite(scaleId)) {
        alert('Select a valid grade scale');
        setScaleEntrySaving(false);
        return;
      }

      const payload = {
        school_id: schoolData.id,
        scale_id: scaleId,
        code: scaleEntryForm.code.trim() || null,
        label: scaleEntryForm.label.trim() || null,
        min_percentage: parsePercentageValue(scaleEntryForm.min_percentage),
        max_percentage: parsePercentageValue(scaleEntryForm.max_percentage)
      };

      const { data, error } = await supabase
        .from('grade_scale_entries')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      setGradeScaleEntries(prev => {
        const existing = prev[data.scale_id] || [];
        const updated = [...existing, data].sort((a, b) => {
          const aMin = a.min_percentage ?? Number.NEGATIVE_INFINITY;
          const bMin = b.min_percentage ?? Number.NEGATIVE_INFINITY;
          return aMin - bMin;
        });

        return {
          ...prev,
          [data.scale_id]: updated
        };
      });

      setScaleEntryForm(prev => ({
        ...prev,
        code: '',
        label: '',
        min_percentage: '',
        max_percentage: ''
      }));
    } catch (err) {
      console.error('Error saving grade scale entry:', err);
      alert('Unable to save grade scale entry right now. Please try again.');
    } finally {
      setScaleEntrySaving(false);
    }
  };

  const getGradeLevelName = (levelId) => {
    return gradeLevels.find(level => level.id === levelId)?.name || 'Level';
  };

  const formatScaleEntryRange = (entry) => {
    const hasMin = entry.min_percentage !== null && entry.min_percentage !== undefined;
    const hasMax = entry.max_percentage !== null && entry.max_percentage !== undefined;

    if (!hasMin && !hasMax) return 'Any score';
    if (hasMin && hasMax) return `${entry.min_percentage}% - ${entry.max_percentage}%`;
    if (hasMin) return `≥ ${entry.min_percentage}%`;
    return `≤ ${entry.max_percentage}%`;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.school_name.trim()) {
      errors.school_name = 'School name is required';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required';
    }

    if (!formData.principal_name.trim()) {
      errors.principal_name = 'Principal name is required';
    }

    if (!formData.principal_email.trim()) {
      errors.principal_email = 'Principal email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.principal_email)) {
      errors.principal_email = 'Please enter a valid email address';
    }

    if (formData.total_students < 0) {
      errors.total_students = 'Total students cannot be negative';
    }

    if (formData.total_teachers < 0) {
      errors.total_teachers = 'Total teachers cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!schoolData?.id) {
      alert('School data not found. Please login again.');
      return;
    }

    setSaving(true);

    try {
      // Upload logo if a new file is selected
      let logoUrl = schoolData?.logo_url || null;
      if (logoFile) {
        try {
          setUploadingLogo(true);
          const fileExt = logoFile.name.split('.').pop();
          const fileName = `school_logo_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `school-logos/${schoolData.id}/${fileName}`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('student-images')
            .upload(filePath, logoFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Logo upload error:', uploadError);
            alert(`Failed to upload logo: ${uploadError.message}`);
            setSaving(false);
            setUploadingLogo(false);
            return;
          }

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('student-images')
            .getPublicUrl(filePath);

          logoUrl = publicUrl;
          console.log('Logo uploaded successfully. URL:', publicUrl);
          console.log('File path:', filePath);
        } catch (logoErr) {
          console.error('Logo upload exception:', logoErr);
          alert('Failed to upload logo. Please try again.');
          setSaving(false);
          setUploadingLogo(false);
          return;
        } finally {
          setUploadingLogo(false);
        }
      }

      const updateData = {
        school_name: formData.school_name.trim(),
        school_type: formData.school_type,
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip_code: formData.zip_code.trim() || null,
        website: formData.website.trim() || null,
        principal_name: formData.principal_name.trim(),
        principal_email: formData.principal_email.trim(),
        total_students: parseInt(formData.total_students) || 0,
        total_teachers: parseInt(formData.total_teachers) || 0,
        description: formData.description.trim() || null,
        currency: formData.currency || 'PKR',
        logo_url: logoUrl
      };

      console.log('Updating school with data:', updateData);
      console.log('Logo URL being saved:', logoUrl);
      
      const { data, error } = await supabase
        .from('schools')
        .update(updateData)
        .eq('id', schoolData.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating school:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        alert(`Failed to update school settings: ${error.message}`);
        setSaving(false);
        return;
      }
      
      console.log('School updated successfully. Response data:', data);
      console.log('Logo URL in response:', data.logo_url);

      // Update local state and localStorage
      const updatedSchoolData = { ...schoolData, ...data };
      setSchoolData(updatedSchoolData);
      localStorage.setItem('schoolData', JSON.stringify(updatedSchoolData));
      
      // Update logo preview with the saved logo URL
      if (data.logo_url) {
        // Try to get valid URL (signed URL if needed)
        const validUrl = await getValidFileUrl(data.logo_url);
        setLogoPreview(validUrl);
        console.log('Logo preview updated with saved URL:', validUrl);
      } else {
        // If logo_url is null/empty, clear preview
        setLogoPreview(null);
      }
      
      // Reset logo file after successful save
      setLogoFile(null);

      setShowSuccessPopup(true);

    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update school settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetBranchForm = () => {
    setBranchForm(INITIAL_BRANCH_FORM);
    setEditingBranchId(null);
  };

  const handleBranchSubmit = async (e) => {
    e.preventDefault();

    if (!branchForm.branch_name.trim()) {
      setBranchError('Branch name is required');
      return;
    }

    if (!schoolData?.id) {
      setBranchError('Unable to determine school context');
      return;
    }

    setBranchSaving(true);
    setBranchError('');

    try {
      const insertPayload = {
        school_id: schoolData.id,
        branch_name: branchForm.branch_name.trim(),
        phone: branchForm.phone.trim() || null,
        address: branchForm.address.trim() || null,
        city: branchForm.city.trim() || null,
        state: branchForm.state.trim() || null,
        zip_code: branchForm.zip_code.trim() || null,
        contact_person: branchForm.contact_person.trim() || null,
        contact_email: branchForm.contact_email.trim() || null,
        contact_phone: branchForm.contact_phone.trim() || null,
        is_active: true
      };

      if (editingBranchId) {
        const { data, error } = await supabase
          .from('branches')
          .update(insertPayload)
          .eq('id', editingBranchId)
          .select()
          .single();

        if (error) {
          console.error('Error updating branch:', error);
          setBranchError('Failed to update branch. Please try again.');
          return;
        }

        setBranches(prev => prev.map(item => (item.id === editingBranchId ? data : item)));
        resetBranchForm();
      } else {
        const { data, error } = await supabase
          .from('branches')
          .insert(insertPayload)
          .select()
          .single();

        if (error) {
          console.error('Error adding branch:', error);
          setBranchError('Failed to save branch. Please try again.');
          return;
        }

        setBranches(prev => [...prev, data]);
        resetBranchForm();
      }
    } catch (err) {
      console.error('Exception adding branch:', err);
      setBranchError('Failed to save branch. Please try again.');
    } finally {
      setBranchSaving(false);
    }
  };

  const handleEditBranch = (branch) => {
    setEditingBranchId(branch.id);
    setBranchForm({
      branch_name: branch.branch_name || '',
      phone: branch.phone || '',
      address: branch.address || '',
      city: branch.city || '',
      state: branch.state || '',
      zip_code: branch.zip_code || '',
      contact_person: branch.contact_person || '',
      contact_email: branch.contact_email || '',
      contact_phone: branch.contact_phone || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteBranch = async (branch) => {
    const confirmDelete = window.confirm(`Delete branch "${branch.branch_name}"? This cannot be undone.`);
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('branches')
        .delete()
        .eq('id', branch.id);

      if (error) {
        console.error('Error deleting branch:', error);
        alert('Failed to delete branch. Please try again.');
        return;
      }

      setBranches(prev => prev.filter(item => item.id !== branch.id));
      if (editingBranchId === branch.id) {
        resetBranchForm();
      }
    } catch (err) {
      console.error('Exception deleting branch:', err);
      alert('Failed to delete branch. Please try again.');
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader
        schoolData={schoolData}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        handleLogout={handleLogout}
      />

      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      <div className="main-content">
        <div className="content-header">
          <h1>School Settings</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>

        <div className="content-body">
          <div className="settings-tabs settings-tabs-panel">
            <button
              className={`settings-tab ${activeTab === 'school' ? 'active' : ''}`}
              onClick={() => setActiveTab('school')}
              type="button"
            >
              School Info
            </button>
            <button
              className={`settings-tab ${activeTab === 'branches' ? 'active' : ''}`}
              onClick={() => setActiveTab('branches')}
              type="button"
            >
              Branches
            </button>
          </div>
          {activeTab === 'school' && (
            <div className="dashboard-content">
            <div className="add-teacher-section">
              <div className="add-teacher-card">
                <form onSubmit={handleSubmit} className="add-teacher-form">
                  <div className="form-section">
                    <h3>School Information</h3>
                    <p style={{ color: '#6B7280', marginBottom: '2rem', fontSize: '0.95rem' }}>
                      Update your school details. Email cannot be changed.
                    </p>

                    {/* Logo Upload Section */}
                    <div className="form-row">
                      <div className="form-group" style={{ width: '100%' }}>
                        <label htmlFor="logo">School Logo</label>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '1rem',
                          alignItems: 'flex-start'
                        }}>
                          {logoPreview && (
                            <div style={{ 
                              position: 'relative', 
                              display: 'inline-block',
                              marginBottom: '0.5rem'
                            }}>
                              <img 
                                src={logoPreview} 
                                alt="School logo preview" 
                                onError={async (e) => {
                                  console.error('Error loading logo image:', logoPreview);
                                  // If it's a Supabase URL and not a data URL, try to get signed URL
                                  if (logoPreview && !logoPreview.startsWith('data:') && logoPreview.includes('supabase.co')) {
                                    try {
                                      const validUrl = await getValidFileUrl(logoPreview);
                                      if (validUrl && validUrl !== logoPreview) {
                                        console.log('Retrying with signed URL:', validUrl);
                                        e.target.src = validUrl;
                                        return;
                                      }
                                    } catch (err) {
                                      console.error('Error retrying logo load:', err);
                                    }
                                  }
                                  // If retry failed or not applicable, hide the image
                                  e.target.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('Logo image loaded successfully');
                                }}
                                style={{ 
                                  maxWidth: '200px', 
                                  maxHeight: '200px', 
                                  minWidth: '150px',
                                  minHeight: '150px',
                                  borderRadius: '8px',
                                  border: '2px solid #E5E7EB',
                                  objectFit: 'contain',
                                  background: '#F9FAFB',
                                  padding: '8px',
                                  display: 'block'
                                }} 
                              />
                              {logoFile && (
                                <button
                                  type="button"
                                  onClick={removeLogo}
                                  style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    right: '-10px',
                                    background: '#EF4444',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    lineHeight: '1',
                                    zIndex: 10
                                  }}
                                  title="Remove logo"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label
                              htmlFor="logo"
                              style={{
                                padding: '0.75rem 1.5rem',
                                background: '#FF8C42',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                display: 'inline-block'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#FFA366';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#FF8C42';
                              }}
                            >
                              {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </label>
                            <input
                              type="file"
                              id="logo"
                              accept="image/*"
                              onChange={handleLogoChange}
                              style={{ display: 'none' }}
                              disabled={uploadingLogo || saving}
                            />
                            {uploadingLogo && (
                              <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>
                                Uploading...
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: '0.85rem', color: '#6B7280', margin: 0 }}>
                            Recommended: Square image, max 5MB (PNG, JPG, or GIF)
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="school_name">School Name *</label>
                        <input
                          type="text"
                          id="school_name"
                          name="school_name"
                          value={formData.school_name}
                          onChange={handleInputChange}
                          className={formErrors.school_name ? 'form-input error' : 'form-input'}
                          placeholder="Enter school name"
                        />
                        {formErrors.school_name && (
                          <span className="error-text">{formErrors.school_name}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="school_type">School Type *</label>
                        <select
                          id="school_type"
                          name="school_type"
                          value={formData.school_type}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                          <option value="charter">Charter</option>
                          <option value="international">International</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          value={schoolData?.email || ''}
                          disabled
                          className="form-input-disabled"
                        />
                        <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.25rem' }}>
                          Email cannot be changed
                        </p>
                      </div>

                      <div className="form-group">
                        <label htmlFor="phone">Phone *</label>
                        <input
                          type="text"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={formErrors.phone ? 'form-input error' : 'form-input'}
                          placeholder="Enter phone number"
                        />
                        {formErrors.phone && (
                          <span className="error-text">{formErrors.phone}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="address">Address *</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className={formErrors.address ? 'form-input error' : 'form-input'}
                          placeholder="Enter address"
                        />
                        {formErrors.address && (
                          <span className="error-text">{formErrors.address}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={formErrors.city ? 'form-input error' : 'form-input'}
                          placeholder="Enter city"
                        />
                        {formErrors.city && (
                          <span className="error-text">{formErrors.city}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor="state">State *</label>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className={formErrors.state ? 'form-input error' : 'form-input'}
                          placeholder="Enter state"
                        />
                        {formErrors.state && (
                          <span className="error-text">{formErrors.state}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="zip_code">Zip Code</label>
                        <input
                          type="text"
                          id="zip_code"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Enter zip code"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="website">Website</label>
                        <input
                          type="text"
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Enter website URL"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="currency">Currency *</label>
                        <select
                          id="currency"
                          name="currency"
                          value={formData.currency}
                          onChange={handleInputChange}
                          className="form-input"
                        >
                          <option value="PKR">PKR - Pakistani Rupee (₨)</option>
                          <option value="USD">USD - US Dollar ($)</option>
                          <option value="EUR">EUR - Euro (€)</option>
                          <option value="GBP">GBP - British Pound (£)</option>
                          <option value="AED">AED - UAE Dirham (د.إ)</option>
                          <option value="SAR">SAR - Saudi Riyal (﷼)</option>
                          <option value="QAR">QAR - Qatari Riyal (﷼)</option>
                          <option value="KWD">KWD - Kuwaiti Dinar (د.ك)</option>
                          <option value="BHD">BHD - Bahraini Dinar (.د.ب)</option>
                          <option value="OMR">OMR - Omani Rial (﷼)</option>
                          <option value="JOD">JOD - Jordanian Dinar (د.ا)</option>
                          <option value="LBP">LBP - Lebanese Pound (ل.ل)</option>
                          <option value="EGP">EGP - Egyptian Pound (£)</option>
                          <option value="ILS">ILS - Israeli Shekel (₪)</option>
                          <option value="TRY">TRY - Turkish Lira (₺)</option>
                          <option value="IRR">IRR - Iranian Rial (﷼)</option>
                          <option value="IQD">IQD - Iraqi Dinar (ع.د)</option>
                          <option value="AFN">AFN - Afghan Afghani (؋)</option>
                          <option value="INR">INR - Indian Rupee (₹)</option>
                          <option value="CAD">CAD - Canadian Dollar (C$)</option>
                          <option value="AUD">AUD - Australian Dollar (A$)</option>
                          <option value="JPY">JPY - Japanese Yen (¥)</option>
                          <option value="CNY">CNY - Chinese Yuan (¥)</option>
                          <option value="SGD">SGD - Singapore Dollar (S$)</option>
                          <option value="MYR">MYR - Malaysian Ringgit (RM)</option>
                          <option value="THB">THB - Thai Baht (฿)</option>
                          <option value="IDR">IDR - Indonesian Rupiah (Rp)</option>
                          <option value="PHP">PHP - Philippine Peso (₱)</option>
                          <option value="VND">VND - Vietnamese Dong (₫)</option>
                          <option value="BDT">BDT - Bangladeshi Taka (৳)</option>
                          <option value="LKR">LKR - Sri Lankan Rupee (₨)</option>
                          <option value="NPR">NPR - Nepalese Rupee (₨)</option>
                          <option value="KES">KES - Kenyan Shilling (KSh)</option>
                          <option value="NGN">NGN - Nigerian Naira (₦)</option>
                          <option value="ZAR">ZAR - South African Rand (R)</option>
                        </select>
                        <p style={{ fontSize: '0.85rem', color: '#6B7280', marginTop: '0.25rem' }}>
                          Select the currency used for fees and payments
                        </p>
                      </div>
                    </div>

                    <div className="form-section" style={{ marginTop: '2rem' }}>
                      <h3>Principal Information</h3>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="principal_name">Principal Name *</label>
                          <input
                            type="text"
                            id="principal_name"
                            name="principal_name"
                            value={formData.principal_name}
                            onChange={handleInputChange}
                            className={formErrors.principal_name ? 'form-input error' : 'form-input'}
                            placeholder="Enter principal name"
                          />
                          {formErrors.principal_name && (
                            <span className="error-text">{formErrors.principal_name}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="principal_email">Principal Email *</label>
                          <input
                            type="email"
                            id="principal_email"
                            name="principal_email"
                            value={formData.principal_email}
                            onChange={handleInputChange}
                            className={formErrors.principal_email ? 'form-input error' : 'form-input'}
                            placeholder="Enter principal email"
                          />
                          {formErrors.principal_email && (
                            <span className="error-text">{formErrors.principal_email}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="form-section" style={{ marginTop: '2rem' }}>
                      <h3>School Statistics</h3>

                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="total_students">Total Students</label>
                          <input
                            type="number"
                            id="total_students"
                            name="total_students"
                            value={formData.total_students}
                            onChange={handleInputChange}
                            className={formErrors.total_students ? 'form-input error' : 'form-input'}
                            placeholder="Enter total students"
                            min="0"
                          />
                          {formErrors.total_students && (
                            <span className="error-text">{formErrors.total_students}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="total_teachers">Total Teachers</label>
                          <input
                            type="number"
                            id="total_teachers"
                            name="total_teachers"
                            value={formData.total_teachers}
                            onChange={handleInputChange}
                            className={formErrors.total_teachers ? 'form-input error' : 'form-input'}
                            placeholder="Enter total teachers"
                            min="0"
                          />
                          {formErrors.total_teachers && (
                            <span className="error-text">{formErrors.total_teachers}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="form-section" style={{ marginTop: '2rem' }}>
                      <h3>Description</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="form-textarea"
                            rows="4"
                            placeholder="Enter school description (optional)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => navigate('/school/dashboard')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          )}
          {activeTab === 'branches' && (
            <div className="dashboard-content">
              <div className="add-teacher-section">
                <div className="add-teacher-card">
                  <div className="form-section">
                    <h3>Branches</h3>
                    <p style={{ color: '#6B7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                      Track and add branch locations that belong to this school.
                    </p>

                    <div className="branch-form-card" style={{ marginTop: '2rem' }}>
                      <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Add New Branch</h4>
                      <form onSubmit={handleBranchSubmit} className="add-teacher-form" style={{ padding: 0 }}>
                        <div className="form-row">
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_name">Branch Name *</label>
                            <input
                              type="text"
                              id="branch_name"
                              name="branch_name"
                              value={branchForm.branch_name}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Enter branch name"
                            />
                          </div>
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_phone">Phone</label>
                            <input
                              type="text"
                              id="branch_phone"
                              name="phone"
                              value={branchForm.phone}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Branch phone"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_address">Address</label>
                            <input
                              type="text"
                              id="branch_address"
                              name="address"
                              value={branchForm.address}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Branch address"
                            />
                          </div>
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_city">City</label>
                            <input
                              type="text"
                              id="branch_city"
                              name="city"
                              value={branchForm.city}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="City"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_state">State</label>
                            <input
                              type="text"
                              id="branch_state"
                              name="state"
                              value={branchForm.state}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="State"
                            />
                          </div>
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_zip">Zip Code</label>
                            <input
                              type="text"
                              id="branch_zip"
                              name="zip_code"
                              value={branchForm.zip_code}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Zip code"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_contact_person">Contact Person</label>
                            <input
                              type="text"
                              id="branch_contact_person"
                              name="contact_person"
                              value={branchForm.contact_person}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Contact person"
                            />
                          </div>
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_contact_email">Contact Email</label>
                            <input
                              type="email"
                              id="branch_contact_email"
                              name="contact_email"
                              value={branchForm.contact_email}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Contact email"
                            />
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group" style={{ flex: '1' }}>
                            <label htmlFor="branch_contact_phone">Contact Phone</label>
                            <input
                              type="text"
                              id="branch_contact_phone"
                              name="contact_phone"
                              value={branchForm.contact_phone}
                              onChange={handleBranchInputChange}
                              className="form-input"
                              placeholder="Contact phone"
                            />
                          </div>
                        </div>

                        {branchError && (
                          <p className="error-text" style={{ marginBottom: '1rem' }}>{branchError}</p>
                        )}

                          <div className="form-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <button
                              type="submit"
                              className="submit-btn"
                              disabled={branchSaving}
                            >
                              {branchSaving ? (editingBranchId ? 'Updating...' : 'Saving...') : (editingBranchId ? 'Update Branch' : 'Save Branch')}
                            </button>
                            {!!editingBranchId && (
                              <button
                                type="button"
                                className="cancel-btn"
                                onClick={resetBranchForm}
                              >
                                Cancel edit
                              </button>
                            )}
                          </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>

              <div className="branches-table-wrapper" style={{ marginTop: '2rem' }}>
                {branchLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading saved branches...</p>
                  </div>
                ) : branchError ? null : branches.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">🏫</div>
                    <h3>No branches added yet</h3>
                    <p>Use the form above to add your first branch.</p>
                  </div>
                ) : (
                  <table className="branches-table">
                    <thead>
                      <tr>
                        <th>Branch</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map(branch => (
                        <tr key={branch.id || branch.branch_name}>
                          <td>
                            <strong>{branch.branch_name || 'Branch'}</strong>
                            <div className="branch-subtext">{branch.address || ''}</div>
                          </td>
                          <td>
                            {branch.city ? `${branch.city}, ` : ''}
                            {branch.state || ''}
                            {branch.zip_code ? ` ${branch.zip_code}` : ''}
                          </td>
                          <td>{branch.phone || 'N/A'}</td>
                          <td>
                            {branch.contact_person || 'N/A'}
                            <div className="branch-subtext">
                              {branch.contact_email && <span>{branch.contact_email}</span>}
                              {branch.contact_email && branch.contact_phone && <span> · </span>}
                              {branch.contact_phone && <span>{branch.contact_phone}</span>}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${branch.is_active ? 'active' : 'inactive'}`}>
                              {branch.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <button
                              type="button"
                              className="table-icon-btn edit"
                              onClick={() => handleEditBranch(branch)}
                              title="Edit branch"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              className="table-icon-btn delete"
                              onClick={() => handleDeleteBranch(branch)}
                              title="Delete branch"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showSuccessPopup && (
        <div
          className="popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSuccessPopup(false);
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: '3rem', color: '#FFFFFF' }}>✓</span>
            </div>

            <h2 style={{
              textAlign: 'center',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Settings Updated Successfully!
            </h2>

            <p style={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.95rem',
              marginBottom: '2rem'
            }}>
              Your school settings have been updated
            </p>

            <button
              onClick={() => {
                setShowSuccessPopup(false);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: '#FF8C42',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FFA366';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;

